import { process, State } from '@progress/kendo-data-query';
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridDataStateChangeEvent,
  GridEditChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import React, { ReactElement } from 'react';

import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import {
  EditDescriptor,
  getSelectedState,
  SelectDescriptor,
} from '@progress/kendo-react-data-tools';
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApiEnums } from '../../../Interfaces/ApiEnums';
import {
  ConfigSetting,
  UpdateConfigSetting,
} from '../../../Interfaces/Config/ConfigSetting';
import { NodeConfigInfo } from '../../../Interfaces/Config/NodeConfigInfo';
import { ApplicationState } from '../../../store';
import { editConfigSettings } from '../../../store/Config/Settings/ConfigSettingsActions';
import esMessages from '../../../utils/gridMessageKey.json';
import { isBBoxRectArray, isBBoxRectObject } from '../../../utils/utils';
import { LocaleInterface, locales } from '../../AlertsGrid/GridLocale';
import StringResourceManager, { GetString } from '../../StringResourceManager';
import ApplyScheduleDatePickerDialog from './ApplyScheduleDatePickerDialog';
loadMessages(esMessages, 'fr-CA');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    box: {
      height: 60,
      display: 'flex',
      padding: theme.spacing(2),
      margin: theme.spacing(1),
    },
    spreadBox: {
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    actionButton: {
      backgroundColor: '#153654',
      alignItems: 'center',
      alignContent: 'center',
      width: '200px',
      color: '#fff',
      '&:hover': {
        background: '#153654',
        color: '#fff',
      },
    },
  }),
);

interface Props {
  nodeSettings: NodeConfigInfo[];
  selectedNodeUuids: string[];
  configSettingType: ApiEnums.ConfigSettingsType;
  toggleDialog: () => void;
}

interface EditConfigSetting {
  ID: number;
  Setting: ConfigSetting;
  InEdit: boolean;
  NewValue: string;
  Selected: boolean;
}

const DATA_ITEM_KEY = 'ID';

type GridEditContextType = {
  isNewValueEditable: (row: EditConfigSetting) => boolean;
};

const GridEditContext = React.createContext<GridEditContextType | null>(null);

const NewValueCell = (props: GridCustomCellProps) => {
  const ctx = React.useContext(GridEditContext);
  const row = props.dataItem as EditConfigSetting;
  const value = row.NewValue ?? '';

  const isEditable = ctx?.isNewValueEditable(row);

  return (
    <td {...props.tdProps}>
      <input
        className="k-input k-textbox"
        style={{ width: '100%' }}
        value={value}
        onChange={e => {
          props.onChange?.({
            dataItem: row,
            dataIndex: row.ID,
            field: props.field,
            value: e.target.value,
            syntheticEvent: e,
          });
        }}
        onClick={e => e.stopPropagation()}
      />
    </td>
  );
};

const EditNodeConfigDetailComponent: React.FC<Props> = ({
  nodeSettings,
  selectedNodeUuids: selectedNodeUuids,
  configSettingType,
  toggleDialog,
}): ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const SELECTED_FIELD = 'Selected';

  const initialDataState: State = { group: [{ field: 'Setting.Category' }] };

  const [data, setData] = React.useState<EditConfigSetting[]>([]);

  const [configSettings, setConfigSettings] = React.useState<ConfigSetting[]>(
    [],
  );

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const filter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.nodesConfigFilters,
  );

  const jsonFilter: CompositeFilterDescriptor = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.nodesJsonConfigFilters,
  );

  const [select, setSelect] = React.useState<SelectDescriptor>({});

  const [edit, setEdit] = React.useState<EditDescriptor>({});

  const [
    isApplyScheduleDatePickerDialogVisible,
    setIsApplyScheduleDatePickerDialogVisible,
  ] = React.useState<boolean>(false);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const itemChange = (e: GridItemChangeEvent) => {
    let newData = data.map((item: EditConfigSetting) => {
      if (item.Setting === e.dataItem.Setting) {
        if (e.value != '' && e.value != item.Setting.Value)
          item.Selected = true;
        else item.Selected = false;

        item.NewValue = e.value;
      }

      return item;
    });

    getApplyButtonEnabledStatus(newData);

    setData(newData);
  };

  useEffect(() => {
    getSettingFiltersInfo();
    return () => {};
  }, [selectedNodeUuids]);

  const getSettingFiltersInfo = () => {
    let settings: ConfigSetting[] = [];
    const activeFilter =
      configSettingType === ApiEnums.ConfigSettingsType.JsonSetting
        ? jsonFilter
        : filter;
    activeFilter.filters?.map((x: any) => {
      if (x?.field && x.field == 'ConfigSetting') {
        let settingInfo: ConfigSetting = x?.value;
        if (
          settingInfo?.Category != null &&
          settingInfo?.Category != undefined &&
          settingInfo?.Name != null &&
          settingInfo?.Name != undefined
        ) {
          for (let i = 0; i < selectedNodeUuids.length; i++) {
            let filterExist = settings.findIndex(
              (info: ConfigSetting) =>
                info.Category == settingInfo?.Category &&
                info.Name == settingInfo?.Name,
            );
            if (filterExist == -1) {
              let nodeConfigIndex = nodeSettings.findIndex(
                (info: NodeConfigInfo) =>
                  info.NodeInfo.Uuid == selectedNodeUuids[i],
              );

              let settingIndex = nodeSettings[
                nodeConfigIndex
              ].ConfigSettingList.findIndex(
                (setting: ConfigSetting) =>
                  setting.Category == settingInfo?.Category &&
                  setting.Name == settingInfo?.Name,
              );

              settings.push(
                nodeSettings[nodeConfigIndex].ConfigSettingList[settingIndex],
              );
            }
          }
        }
      }
    });

    setConfigSettings(settings);
  };

  const NON_EDITABLE_FIELDS = new Set([
    'packing',
    'crop',
    'pinpad',
    'staging',
    'scanning',
    'transaction',
  ]);

  useEffect(() => {
    if (!configSettings) return;

    const allEdit: EditDescriptor = {};
    const allData = configSettings.map((item, index) => {
      const name = item?.Name?.toLowerCase();

      const isEditable = !NON_EDITABLE_FIELDS.has(name);

      if (isEditable) {
        allEdit[index] = true;
      }

      return {
        ID: index,
        InEdit: isEditable,
        Setting: item,
        NewValue: '',
        Selected: false,
      };
    });

    setData(allData);
    setEdit(allEdit);
    setApplyButtonEnabled(false);
    return () => {};
  }, [configSettings]);

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelected = getSelectedState({
      event,
      selectedState: select,
      dataItemKey: DATA_ITEM_KEY,
    });

    setSelect(newSelected);

    const selectedSetting = event.dataItem.Setting;
    const newData = data.map(item => {
      if (item.Setting === selectedSetting) {
        item.Selected = !item.Selected;
      }
      return item;
    });

    getApplyButtonEnabledStatus(newData);
    setData(newData);
  };

  const stripCategoryFromPath = (path?: string, category?: string): string => {
    if (!path || !category) return path ?? '';

    const normalizedPath = path.trim().replace(/^\/+|\/+$/g, '');
    const normalizedCategory = category.trim();

    if (normalizedPath === normalizedCategory) {
      return '';
    }

    const prefix = `${normalizedCategory}/`;

    if (normalizedPath.startsWith(prefix)) {
      return normalizedPath.substring(prefix.length);
    }

    return normalizedPath;
  };

  const processRequestCommand = (
    scheduledDateTime: string,
    applyNow: boolean,
  ) => {
    let selectedConfigSettings: ConfigSetting[] = [];

    data.map(item => {
      if (item.Selected) {
        selectedConfigSettings.push({
          ...item.Setting,
          Value: item.NewValue,
          Path: stripCategoryFromPath(item.Setting.Path, item.Setting.Category),
        });
      }
    });

    let updateConfigSetting: UpdateConfigSetting = {
      NodeUuids: selectedNodeUuids,
      Settings: selectedConfigSettings,
      ScheduledDateTime: scheduledDateTime,
      ApplyNow: applyNow,
    };

    editConfigSettings(dispatch, configSettingType, updateConfigSetting);

    toggleDialog();
  };

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;

      const newData = data.map(item => {
        item.Selected = checked;
        return item;
      });

      getApplyButtonEnabledStatus(newData);

      setData(newData);
    },
    [data, dataState],
  );

  const [applyButtonEnabled, setApplyButtonEnabled] =
    React.useState<boolean>(false);

  const getApplyButtonEnabledStatus = (data: EditConfigSetting[]) => {
    let selectedExist = data.findIndex(
      v => v.Selected && v.NewValue != '' && v.Setting.Value != v.NewValue,
    );

    if (selectedExist == -1) setApplyButtonEnabled(false);
    else setApplyButtonEnabled(true);
  };

  const columnWidth = selectedNodeUuids.length == 1 ? '200px' : '250px';

  const applyNowButtonOnClick = () => {
    processRequestCommand(new Date().toISOString(), true);
  };

  const handleEditChange = (event: GridEditChangeEvent) => {
    setEdit(event.edit);
  };

  const isBBoxLikeValue = (raw: string): boolean => {
    if (!raw || typeof raw !== 'string') return false;

    try {
      const parsed = JSON.parse(raw);

      if (isBBoxRectObject(parsed)) return true;
      if (isBBoxRectArray(parsed)) return true;

      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).some(v => {
          return isBBoxRectObject(v) || isBBoxRectArray(v);
        });
      }

      return false;
    } catch {
      return false;
    }
  };

  const isNewValueEditable = React.useCallback(
    (row: EditConfigSetting): boolean => {
      return !isBBoxLikeValue(row.Setting.Value);
    },
    [],
  );

  const gridEditContextValue = React.useMemo(
    () => ({
      isNewValueEditable,
    }),
    [isNewValueEditable],
  );

  return (
    <LocalizationProvider language={currentLocale.language}>
      <GridEditContext.Provider value={gridEditContextValue}>
        <Grid
          style={{ height: '650px', width: '100%', maxWidth: '1250px' }}
          sortable={true}
          filterable={true}
          groupable={true}
          resizable={true}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 50, 100, 500],
          }}
          take={100}
          data={process(data, dataState)}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          edit={edit}
          editable={{ mode: 'incell' }}
          onEditChange={handleEditChange}
          onItemChange={itemChange}
          select={select}
          onHeaderSelectionChange={onHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          dataItemKey={DATA_ITEM_KEY}
        >
          <GridColumn
            filterable={false}
            field={SELECTED_FIELD}
            width={50}
            editable={false}
            hidden
          />
          <GridColumn
            field="Setting.Category"
            title={GetString('COLUMN_TITLE_LANE_CATEGORY')}
            width={columnWidth}
            editable={false}
          />
          <GridColumn
            field="Setting.Name"
            title={GetString('SETTING_NAME')}
            width={columnWidth}
            editable={false}
          />
          {selectedNodeUuids.length == 1 ? (
            <GridColumn
              field="Setting.Value"
              title={GetString('CURRENT_VALUE')}
              width="240px"
              editable={false}
            />
          ) : null}
          <GridColumn
            field="NewValue"
            editable={true}
            editor="text"
            title={GetString('NEW_VALUE')}
            width="280px"
            cells={{ data: NewValueCell }}
          />
        </Grid>
      </GridEditContext.Provider>
      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          color="primary"
          type="submit"
          onClick={applyNowButtonOnClick}
          className={classes.actionButton}
          disabled={!applyButtonEnabled}
        >
          <StringResourceManager stringKey={'APPLY_NOW'} />
        </Button>
        <Button
          color="primary"
          type="submit"
          onClick={() => setIsApplyScheduleDatePickerDialogVisible(true)}
          className={classes.actionButton}
          disabled={!applyButtonEnabled}
        >
          <StringResourceManager stringKey={'APPLY_SCHEDULE'} />
        </Button>

        <Button
          color="primary"
          type="submit"
          onClick={toggleDialog}
          className={classes.actionButton}
        >
          <StringResourceManager stringKey={'CANCEL_BUTTON'} />
        </Button>
      </Box>
      {isApplyScheduleDatePickerDialogVisible && (
        <ApplyScheduleDatePickerDialog
          processRequestCommand={processRequestCommand}
          handleCloseDialog={(e: boolean) =>
            setIsApplyScheduleDatePickerDialogVisible(e)
          }
        />
      )}
    </LocalizationProvider>
  );
};

export default EditNodeConfigDetailComponent;
